import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { 
  Calendar, MapPin, Clock, Users, ArrowLeft, ExternalLink,
  CheckCircle, XCircle, AlertCircle, Edit, Trash2, UserCheck, Send, Copy,
  Paperclip, FileText, HandHeart
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myPlayers, setMyPlayers] = useState([]);
  const [submittingRsvp, setSubmittingRsvp] = useState(false);
  const [submittingParentRsvp, setSubmittingParentRsvp] = useState(false);
  const [markingAttendanceFor, setMarkingAttendanceFor] = useState(null);

  // Linked volunteer opportunities (Phase B — manual link only).
  const [linkedOpportunities, setLinkedOpportunities] = useState([]);
  const [signingUpOpp, setSigningUpOpp] = useState(null);

  // Phase J — Send Reminder modal state
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderAudience, setReminderAudience] = useState('all_invited');
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderToast, setReminderToast] = useState(null); // { type, text }

  useEffect(() => {
    fetchEventDetail();
    // Always try to fetch linked players — /api/dashboard/parent returns
    // {players: []} for users with no parent_guardians record, so this is
    // safe for non-parent (admin / coach / staff) users too. Lets the
    // per-player RSVP card render whenever the caller actually has players,
    // independent of their global role.
    if (user) {
      fetchMyPlayers();
      fetchLinkedOpportunities();
    }
  }, [eventId]);

  const fetchEventDetail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/${eventId}`, {
        withCredentials: true
      });
      setEvent(response.data);
    } catch (error) {
      console.error('Failed to fetch event:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedOpportunities = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/events/${eventId}/volunteer-opportunities`,
        { withCredentials: true },
      );
      setLinkedOpportunities(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      // 404 (event not found) or 401 is fine — silently no-op.
      setLinkedOpportunities([]);
    }
  };

  const handleVolunteerSignup = async (opportunityId) => {
    setSigningUpOpp(opportunityId);
    try {
      await axios.post(
        `${API_URL}/api/volunteer/signup`,
        { opportunity_id: opportunityId, notes: '' },
        { withCredentials: true },
      );
      await fetchLinkedOpportunities();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to sign up');
    } finally {
      setSigningUpOpp(null);
    }
  };

  const fetchMyPlayers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/parent`, {
        withCredentials: true
      });
      setMyPlayers(response.data.players || []);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    }
  };

  const handleRsvp = async (playerId, response) => {
    setSubmittingRsvp(true);
    try {
      await axios.post(`${API_URL}/api/rsvp`, {
        event_id: eventId,
        player_id: playerId,
        response: response
      }, { withCredentials: true });
      fetchEventDetail();
    } catch (error) {
      alert('Failed to submit RSVP');
    } finally {
      setSubmittingRsvp(false);
    }
  };

  // Per-parent RSVP for parent-level event types (volunteer / community /
  // fundraiser / meeting / other). Backend decides eligibility via rsvp_mode.
  const handleParentRsvp = async (response) => {
    setSubmittingParentRsvp(true);
    try {
      await axios.post(`${API_URL}/api/rsvp/parent`, {
        event_id: eventId,
        response: response
      }, { withCredentials: true });
      fetchEventDetail();
    } catch (error) {
      console.error('Failed to submit parent RSVP:', error);
      alert(error?.response?.data?.detail || 'Failed to submit RSVP');
    } finally {
      setSubmittingParentRsvp(false);
    }
  };

  const handleExportCSV = () => {
    if (!event?.guest_list?.length) return;

    const RSVP_LABEL_LOCAL = { attending: 'Attending', absent: 'Not Going', late: 'Late', no_response: 'No Response' };
    const ATT_LABEL_LOCAL = { present: 'Present', absent: 'Absent', late: 'Late', excused: 'Excused', not_marked: 'Not Marked' };

    const csvEscape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val).replace(/\r?\n/g, ' ');
      return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const startIso = event.start_time || '';
    const eventDate = startIso ? startIso.slice(0, 10) : '';
    const audience = event.invited_player_ids?.length
      ? `Custom (${event.invited_player_ids.length} players)`
      : event.team_id ? `Team: ${event.team?.name || event.team_id}`
      : event.division_id ? `Division: ${event.division_id}`
      : event.org_wide ? 'League-wide'
      : 'No audience';

    const rs = event.rsvp_summary || {};
    const as = event.attendance_summary || {};

    const headerLines = [
      ['Event', csvEscape(event.title || '')].join(','),
      ['Date', csvEscape(eventDate)].join(','),
      ['Type', csvEscape(event.event_type || '')].join(','),
      ['Location', csvEscape(event.location || '')].join(','),
      ['Audience', csvEscape(audience)].join(','),
      ['Summary', csvEscape(
        `Invited ${rs.invited ?? event.invited_count ?? 0} | Attending ${rs.attending ?? 0} | Not Going ${rs.absent ?? 0} | Late ${rs.late ?? 0} | No Response ${rs.no_response ?? 0} | Present ${as.present ?? 0} | Absent ${as.absent ?? 0} | Late ${as.late ?? 0} | Excused ${as.excused ?? 0} | Not Marked ${as.not_marked ?? 0}`
      )].join(','),
      '',
    ];
    const columns = ['Player Name', 'Team', 'RSVP', 'RSVP Notes', 'Attendance', 'Attendance Notes', 'Attendance Marked At'];
    const dataRows = event.guest_list.map(g => {
      const att = (event.attendance || []).find(a => a.player_id === g.player_id) || {};
      return [
        csvEscape(g.name || g.player_id),
        csvEscape(g.team_name || ''),
        csvEscape(RSVP_LABEL_LOCAL[g.rsvp_response] || g.rsvp_response || 'No Response'),
        csvEscape(g.rsvp_notes || ''),
        csvEscape(ATT_LABEL_LOCAL[g.attendance_status] || g.attendance_status || 'Not Marked'),
        csvEscape(g.attendance_notes || ''),
        csvEscape(att.marked_at || ''),
      ].join(',');
    });

    const csv = [...headerLines, columns.join(','), ...dataRows].join('\r\n');
    const slug = (event.title || 'event')
      .toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'event';
    const filename = `${slug}-${eventDate || 'undated'}-attendance.csv`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleMarkAttendance = async (playerId, status) => {
    setMarkingAttendanceFor(playerId);
    try {
      await axios.post(`${API_URL}/api/attendance/mark`, {
        event_id: eventId,
        player_id: playerId,
        status,
      }, { withCredentials: true });
      await fetchEventDetail();
    } catch (error) {
      alert('Failed to mark attendance');
    } finally {
      setMarkingAttendanceFor(null);
    }
  };

  const handleCancelEvent = async () => {
    const reason = prompt('Enter cancellation reason:');
    if (!reason) return;
    
    try {
      await axios.post(`${API_URL}/api/events/${eventId}/cancel?reason=${encodeURIComponent(reason)}`, {}, {
        withCredentials: true
      });
      fetchEventDetail();
    } catch (error) {
      alert('Failed to cancel event');
    }
  };

  // Hard delete (admins only). Removes the event + its linked RSVPs,
  // attendance, change logs, and event_reminder announcements. Cannot be
  // undone. Coaches still use the Cancel button (soft, recoverable).
  const handleDeleteEvent = async () => {
    if (!window.confirm(
      `Permanently delete "${event.title}"?\n\n` +
      `This removes the event AND its RSVPs, attendance, change logs, and ` +
      `reminder messages. This cannot be undone.`
    )) return;
    try {
      const res = await axios.delete(`${API_URL}/api/events/${eventId}`, { withCredentials: true });
      const d = res.data.deletes || {};
      alert(
        `Deleted "${event.title}".\n\n` +
        `Removed: ${d.rsvps || 0} RSVPs, ${d.attendance || 0} attendance rows, ` +
        `${d.event_change_logs || 0} change logs, ${d.event_reminders || 0} reminders.`
      );
      navigate('/schedule');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      practice: 'bg-light-blue text-white',
      game: 'bg-red text-white',
      meeting: 'bg-navy text-white',
      equipment_day: 'bg-gold text-navy',
      orientation: 'bg-purple-600 text-white',
      fundraiser: 'bg-green-600 text-white',
      photo_day: 'bg-pink-600 text-white',
      community: 'bg-teal-600 text-white',
      scrimmage: 'bg-orange-600 text-white',
      other: 'bg-gray-600 text-white'
    };
    return colors[type] || colors.other;
  };

  const getPlayerRsvp = (playerId) => {
    return event?.rsvps?.find(r => r.player_id === playerId);
  };

  // Phase J — Send Reminder helpers
  const REMINDER_AUDIENCES = [
    { value: 'all_invited', label: 'All Invited',     icon: '📣', countKey: 'invited' },
    { value: 'no_response', label: 'No Response yet', icon: '⏳', countKey: 'no_response' },
    { value: 'attending',   label: 'Attending',       icon: '✅', countKey: 'attending' },
    { value: 'not_going',   label: 'Not Going',       icon: '❌', countKey: 'absent' },
    { value: 'late',        label: 'Running Late',    icon: '🕐', countKey: 'late' },
  ];

  const reminderAudienceCount = (audKey) => {
    const summary = event?.rsvp_summary || {};
    if (audKey === 'invited') {
      return summary.invited ?? event?.guest_list?.length ?? 0;
    }
    return summary[audKey] ?? 0;
  };

  // Build the default reminder body for the composer prefill. Mirrors
  // the backend default in routes_step4_5.py so what the admin sees is
  // exactly what would be sent if they didn't edit anything.
  const buildDefaultReminderBody = () => {
    if (!event) return '';
    const parts = [event.title || 'Event'];
    if (event.start_time) {
      try {
        parts.push(`📅 ${format(parseISO(event.start_time), 'EEE, MMM d, yyyy • h:mm a')}`);
      } catch {
        parts.push(`📅 ${event.start_time}`);
      }
    }
    const loc = event.location_name || event.location;
    if (loc) parts.push(`📍 ${loc}`);
    parts.push('Please review your RSVP / attendance for this event.');
    return parts.join('\n\n');
  };

  const openReminderModal = () => {
    setReminderAudience('all_invited');
    setReminderTitle(`Reminder: ${event?.title || 'Event'}`);
    setReminderMessage(buildDefaultReminderBody());
    setReminderToast(null);
    setShowReminderModal(true);
  };

  const handleSendReminder = async () => {
    if (sendingReminder) return;
    const trimmedTitle = reminderTitle.trim();
    const trimmedBody = reminderMessage.trim();
    // UI guard — backend retains its own fallback defaulting as a
    // safety net, but composer parity blocks whitespace-only submits.
    if (!trimmedTitle || !trimmedBody) return;
    setSendingReminder(true);
    try {
      const payload = {
        audience: reminderAudience,
        title: trimmedTitle,
        body: trimmedBody,
      };
      const res = await axios.post(
        `${API_URL}/api/events/${eventId}/send-reminder`,
        payload,
        { withCredentials: true }
      );
      const smsLine = (() => {
        const s = res.data?.sms || {};
        if (!s.enabled) return ' SMS: disabled.';
        if (s.sent > 0) return ` SMS: sent to ${s.sent} consented parent${s.sent === 1 ? '' : 's'}.`;
        if (s.reason === 'quiet_hours') return ' SMS: skipped (quiet hours).';
        if (s.reason === 'daily_cap_reached') return ' SMS: skipped (daily cap).';
        return ' SMS: no consented recipients.';
      })();
      setReminderToast({
        type: 'success',
        text: `Reminder sent to ${res.data.recipient_count} parent${res.data.recipient_count === 1 ? '' : 's'}.${smsLine}`,
      });
      // Composer parity: close the modal on success but keep the event
      // page mounted underneath. Toast is cleared on next open.
      setShowReminderModal(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to send reminder';
      setReminderToast({ type: 'error', text: detail });
    } finally {
      setSendingReminder(false);
    }
  };

  const isAdmin = user?.role === 'league_admin' || user?.role === 'super_admin';
  // Team-scoped capability: a user is a coach/team_mom for THIS event when
  // they hold that role on the event's team (or any team if the event is
  // org-wide/division-wide and they hold ANY such role).
  const teamAssignments = user?.team_assignments || [];
  const assignmentForEventTeam = event?.team_id
    ? teamAssignments.find((a) => a.team_id === event.team_id)
    : null;
  const isCoach = assignmentForEventTeam
    ? (assignmentForEventTeam.team_role === 'head_coach' || assignmentForEventTeam.team_role === 'assistant_coach')
    : teamAssignments.some((a) => a.team_role === 'head_coach' || a.team_role === 'assistant_coach');
  const isTeamMom = assignmentForEventTeam
    ? assignmentForEventTeam.team_role === 'team_mom'
    : teamAssignments.some((a) => a.team_role === 'team_mom');
  const canMarkAttendance = isAdmin || isCoach || isTeamMom;

  // Active Parent/Admin view (mirrors SchedulePage). Super-admins toggled
  // into "Parent" view should not see Edit/Cancel/Delete action buttons
  // even though their global role still allows them.
  const activeView = (typeof window !== 'undefined' && window.sessionStorage)
    ? (sessionStorage.getItem('pcc_active_view') || 'admin')
    : 'admin';
  const inParentView = activeView === 'parent';

  // Practice management is admin-only (league_admin / super_admin). Coaches
  // lose Edit + Cancel on practices. Non-practice events keep the existing
  // (isAdmin || isCoach) rule.
  const isPractice = event?.event_type === 'practice';
  const canEditEvent = !inParentView && (isPractice ? isAdmin : (isAdmin || isCoach));
  const canCancelEvent = canEditEvent;
  const canDeleteEvent = !inParentView && isAdmin;

  const RSVP_LABEL = { attending: 'Attending', absent: 'Not Going', late: 'Late', no_response: 'No Response' };
  const ATT_LABEL = { present: 'Present', absent: 'Absent', late: 'Late', excused: 'Excused', not_marked: 'Not Marked' };
  const rsvpBadgeClass = (r) => ({
    attending: 'bg-green-100 text-green-800',
    absent: 'bg-red/10 text-red',
    late: 'bg-yellow-100 text-yellow-800',
  }[r] || 'bg-gray-100 text-gray-600');
  const attBadgeClass = (s) => ({
    present: 'bg-green-100 text-green-800',
    absent: 'bg-red/10 text-red',
    late: 'bg-yellow-100 text-yellow-800',
    excused: 'bg-gray-200 text-gray-700',
  }[s] || 'bg-gray-100 text-gray-500');

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  if (!event) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">Event not found</p>
          <Button onClick={() => navigate('/schedule')} className="mt-4">
            Back to Schedule
          </Button>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="event-detail-page">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/schedule')}
          className="flex items-center text-gray-600 hover:text-navy mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Schedule
        </button>

        {/* Canceled/Updated Banner */}
        {event.status === 'canceled' && (
          <div className="bg-red/10 border border-red/20 rounded-lg p-4 mb-6">
            <div className="flex items-center text-red font-medium mb-1">
              <XCircle className="w-5 h-5 mr-2" />
              This event has been canceled
            </div>
            {event.cancellation_reason && (
              <p className="text-red/80 text-sm ml-7">{event.cancellation_reason}</p>
            )}
          </div>
        )}

        {event.status === 'updated' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center text-yellow-800 font-medium">
              <AlertCircle className="w-5 h-5 mr-2" />
              This event has been updated. Please review the details.
            </div>
          </div>
        )}

        {/* Event Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Badge className={getEventTypeColor(event.event_type)}>
                    {event.event_type.replace(/_/g, ' ')}
                  </Badge>
                  {event.org_wide && (
                    <Badge variant="outline">Organization-Wide</Badge>
                  )}
                </div>
                <h1 className={`text-2xl sm:text-3xl font-bold text-navy ${
                  event.status === 'canceled' ? 'line-through opacity-60' : ''
                }`}>
                  {event.title}
                </h1>
                {event.team?.name && (
                  <p className="text-gray-600 mt-1">{event.team.name}</p>
                )}
              </div>
              
              {(canEditEvent || canDeleteEvent) && event.status !== 'canceled' && (
                <div className="flex items-center space-x-2 mt-4 sm:mt-0">
                  {canEditEvent && (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/admin/events/${eventId}/edit`)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  {canEditEvent && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/events/new?source=${eventId}`)}
                      data-testid="duplicate-event-btn"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Duplicate
                    </Button>
                  )}
                  {canCancelEvent && (
                    <Button variant="outline" size="sm" className="text-red border-red hover:bg-red/10" onClick={handleCancelEvent}>
                      <Trash2 className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  )}
                  {canDeleteEvent && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red border-red hover:bg-red/10"
                      onClick={handleDeleteEvent}
                      data-testid="delete-event-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Image / Flyer */}
            {event.image_url && (
              <Card>
                <CardContent className="p-0 overflow-hidden rounded-lg">
                  <img
                    src={event.image_url}
                    alt={`${event.title} flyer`}
                    className="w-full max-h-96 object-contain bg-gray-50"
                    data-testid="event-detail-image"
                  />
                </CardContent>
              </Card>
            )}

            {/* Attachment (PDF/document) — single slot, any event type */}
            {event.attachment_url && (
              <Card>
                <CardContent className="p-4">
                  <a
                    href={event.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-navy hover:bg-gray-50 rounded-lg p-2 -m-2"
                    data-testid="event-detail-attachment"
                  >
                    <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-navy" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">
                        {event.attachment_filename || 'Attachment'}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" />
                        Tap to open
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Linked Volunteer Roles — manual / optional link. Renders
                only when an admin has explicitly linked one or more
                volunteer opportunities to this event. No auto-creation. */}
            {linkedOpportunities.length > 0 && (
              <Card data-testid="event-volunteer-roles-card">
                <CardHeader>
                  <CardTitle className="text-navy text-lg flex items-center gap-2">
                    <HandHeart className="w-5 h-5" />
                    Volunteer Roles
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    Help out at this event by signing up below.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {linkedOpportunities.map((opp) => {
                      const isFull = opp.status === 'full' || opp.available_slots === 0;
                      const signedUp = opp.user_signed_up;
                      return (
                        <div
                          key={opp.opportunity_id}
                          className="p-3 border rounded-lg flex items-start justify-between gap-3 flex-wrap"
                          data-testid={`event-volunteer-row-${opp.opportunity_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-navy">{opp.title}</p>
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {opp.start_time}{opp.end_time ? ` – ${opp.end_time}` : ''}
                              </span>
                              {opp.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {opp.location}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {opp.filled_slots}/{opp.num_slots}
                                {typeof opp.spots_remaining === 'number' && opp.spots_remaining > 0 && (
                                  <span className="text-gray-400"> · {opp.spots_remaining} left</span>
                                )}
                              </span>
                              {opp.role_type && (
                                <span className="text-gray-400 capitalize">
                                  · {String(opp.role_type).replace('_', ' ')}
                                </span>
                              )}
                            </p>
                            {opp.instructions && (
                              <p className="text-xs text-gray-600 mt-2">{opp.instructions}</p>
                            )}
                            {/* Feb 2026 — privacy-safe volunteer roster per opportunity.
                                Parents see "James G." + role_title only. Backend
                                strips email / phone / household / internal notes. */}
                            {Array.isArray(opp.signups) && opp.signups.length > 0 && (
                              <ul
                                className="mt-2 space-y-1 text-xs"
                                data-testid={`event-volunteer-roster-${opp.opportunity_id}`}
                              >
                                {opp.signups.map((s) => (
                                  <li
                                    key={s.signup_id}
                                    className="flex items-center gap-2 text-gray-700"
                                    data-testid={`event-volunteer-roster-row-${s.signup_id}`}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-navy/60" />
                                    <span className="font-medium">{s.display_name}</span>
                                    {s.role_title && (
                                      <span className="text-gray-500">— {s.role_title}</span>
                                    )}
                                    {s.status && s.status !== 'confirmed' && (
                                      <span className="text-gray-400 capitalize">
                                        ({String(s.status).replace('_', ' ')})
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            {signedUp ? (
                              <Badge
                                className="bg-green-100 text-green-700"
                                data-testid={`event-volunteer-signed-up-${opp.opportunity_id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Signed Up
                              </Badge>
                            ) : isFull ? (
                              <Badge variant="secondary" className="bg-gray-200">Full</Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleVolunteerSignup(opp.opportunity_id)}
                                disabled={signingUpOpp === opp.opportunity_id}
                                className="bg-navy hover:bg-navy/90"
                                data-testid={`event-volunteer-signup-${opp.opportunity_id}`}
                              >
                                {signingUpOpp === opp.opportunity_id ? 'Signing...' : 'Sign Up'}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Date/Time/Location */}
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <p className="font-medium text-navy">
                      {format(parseISO(event.start_time), 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-gray-600">
                      {format(parseISO(event.start_time), 'h:mm a')}
                      {event.end_time && ` - ${format(parseISO(event.end_time), 'h:mm a')}`}
                    </p>
                  </div>
                </div>

                {event.location_name && (
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-navy" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">{event.location_name}</p>
                      {event.address && <p className="text-gray-600">{event.address}</p>}
                      {event.map_link && (
                        <a 
                          href={event.map_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-light-blue hover:underline text-sm inline-flex items-center mt-1"
                        >
                          View on Map <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Description */}
            {event.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-navy text-lg">Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>
                  {event.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{event.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* RSVP for linked players — RETIRED. Parent self-response now
                uses the universal "Your Attendance" card below for every
                event type (Community Day rule). The admin/coach Guest
                List + per-player attendance marking surface is preserved
                lower on the page. */}

            {/* Your Attendance — per-parent (self) mode (volunteer / community /
                fundraiser / meeting / other). Open to ANY authenticated user
                so admins, coaches, team moms, and parents can all RSVP for
                themselves. */}
            {event.supports_parent_rsvp && event.status !== 'canceled' && (() => {
              const cur = event.my_parent_rsvp?.response || 'no_response';
              const pill = {
                attending:   { cls: 'bg-green-100 text-green-800',   label: 'Going' },
                late:        { cls: 'bg-yellow-100 text-yellow-800', label: 'Late' },
                absent:      { cls: 'bg-red-100 text-red-800',       label: 'Not Going' },
                no_response: { cls: 'bg-gray-100 text-gray-700',     label: 'No Response' },
              }[cur] || { cls: 'bg-gray-100 text-gray-700', label: 'No Response' };
              const isActive = (r) => cur === r;
              return (
                <Card data-testid="parent-rsvp-card">
                  <CardHeader>
                    <CardTitle className="text-navy text-lg">Your Attendance</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose your response below. You can change it at any time.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 border rounded-lg flex items-center justify-between flex-wrap gap-3" data-testid="parent-rsvp-row">
                      <div className="min-w-0">
                        <p className="font-medium text-navy">
                          {user?.name || 'Your response'}
                        </p>
                        <Badge
                          className={`mt-1 ${pill.cls}`}
                          data-testid="my-parent-rsvp-status"
                        >
                          {pill.label}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleParentRsvp('attending')}
                          disabled={submittingParentRsvp || isActive('attending')}
                          aria-pressed={isActive('attending')}
                          className={isActive('attending')
                            ? 'bg-green-600 text-white cursor-default'
                            : 'bg-gray-200 text-gray-700 hover:bg-green-600 hover:text-white'}
                          data-testid="parent-rsvp-btn-attending"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Going
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleParentRsvp('late')}
                          disabled={submittingParentRsvp || isActive('late')}
                          aria-pressed={isActive('late')}
                          className={isActive('late')
                            ? 'bg-yellow-500 text-white cursor-default'
                            : 'bg-gray-200 text-gray-700 hover:bg-yellow-500 hover:text-white'}
                          data-testid="parent-rsvp-btn-late"
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          Late
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleParentRsvp('absent')}
                          disabled={submittingParentRsvp || isActive('absent')}
                          aria-pressed={isActive('absent')}
                          className={isActive('absent')
                            ? 'bg-red-600 text-white cursor-default'
                            : 'bg-gray-200 text-gray-700 hover:bg-red-600 hover:text-white'}
                          data-testid="parent-rsvp-btn-absent"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Not Going
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Guest List & Attendance — invitee-by-invitee operational view */}
            {/* Hidden from regular parents on player events (it duplicates the
                "RSVP for Your Children" card above). Visible to admins,
                coaches, team_staff, team_mom — and to anyone when attendance
                is enabled (operational check-in view). */}
            {(event.guest_list?.length > 0)
              && (event.rsvp_required || event.attendance_enabled)
              && (isAdmin || canMarkAttendance || event.attendance_enabled) && (
              <Card data-testid="event-guest-list-card">
                <CardHeader>
                  <CardTitle className="text-navy text-lg flex items-center justify-between gap-2">
                    <span className="flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Guest List ({event.guest_list.length})
                    </span>
                    {canMarkAttendance && (
                      <div className="flex gap-2">
                        {isAdmin && (
                          <Button
                            size="sm"
                            className="h-8 px-3 text-xs bg-navy hover:bg-navy/90 text-white"
                            onClick={openReminderModal}
                            data-testid="event-send-reminder-btn"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Send Reminder
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 text-xs"
                          onClick={handleExportCSV}
                          data-testid="event-export-csv-button"
                        >
                          Download CSV
                        </Button>
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="event-guest-list">
                    {event.guest_list.map(g => (
                      <div
                        key={g.player_id}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-gray-50 rounded border border-gray-100"
                        data-testid={`guest-row-${g.player_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-navy truncate">{g.name || g.player_id}</p>
                          {g.team_name && (
                            <p className="text-xs text-gray-500 truncate">{g.team_name}</p>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {event.rsvp_required && (
                            <Badge
                              className={`text-xs ${rsvpBadgeClass(g.rsvp_response)}`}
                              data-testid={`guest-rsvp-${g.player_id}`}
                            >
                              RSVP: {RSVP_LABEL[g.rsvp_response] || 'No Response'}
                            </Badge>
                          )}
                          {event.attendance_enabled && (
                            <Badge
                              className={`text-xs ${attBadgeClass(g.attendance_status)}`}
                              data-testid={`guest-attendance-${g.player_id}`}
                            >
                              {ATT_LABEL[g.attendance_status] || 'Not Marked'}
                            </Badge>
                          )}
                        </div>
                        {canMarkAttendance && event.attendance_enabled && event.status !== 'canceled' && (
                          <div className="flex flex-wrap gap-1 sm:ml-2">
                            {['present', 'absent', 'late', 'excused'].map(s => (
                              <Button
                                key={s}
                                size="sm"
                                variant={g.attendance_status === s ? 'default' : 'outline'}
                                className={`h-7 px-2 text-xs ${
                                  g.attendance_status === s
                                    ? (s === 'present' ? 'bg-green-600 hover:bg-green-700'
                                       : s === 'absent' ? 'bg-red hover:bg-red/90 text-white'
                                       : s === 'late' ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                       : 'bg-gray-600 hover:bg-gray-700 text-white')
                                    : ''
                                }`}
                                disabled={markingAttendanceFor === g.player_id}
                                onClick={() => handleMarkAttendance(g.player_id, s)}
                                data-testid={`mark-${s}-${g.player_id}`}
                              >
                                {ATT_LABEL[s]}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* RSVP Summary — per-player rollup. Hidden on player event
                types (practice / game / scrimmage / equipment_day /
                photo_day) because parents no longer respond per child
                under the universal Community Day rule, so the rollup
                would render as an empty 0/0/0 ghost. The admin/coach
                Guest List + Attendance Summary lower on the page is the
                authoritative per-player signal. */}
            {event.rsvp_required && event.rsvp_summary && !event.supports_player_rsvp && (
              <Card data-testid="event-rsvp-summary-card">
                <CardHeader>
                  <CardTitle className="text-navy text-lg flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    RSVPs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between" data-testid="rsvp-count-invited">
                      <span className="flex items-center text-navy">
                        <Users className="w-4 h-4 mr-2" />
                        Invited
                      </span>
                      <span className="font-bold text-lg">{event.rsvp_summary.invited ?? event.invited_count ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="rsvp-count-attending">
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Attending
                      </span>
                      <span className="font-bold text-lg">{event.rsvp_summary.attending}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="rsvp-count-not-going">
                      <span className="flex items-center text-red">
                        <XCircle className="w-4 h-4 mr-2" />
                        Not Going
                      </span>
                      <span className="font-bold text-lg">{event.rsvp_summary.absent}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="rsvp-count-late">
                      <span className="flex items-center text-yellow-600">
                        <Clock className="w-4 h-4 mr-2" />
                        Late
                      </span>
                      <span className="font-bold text-lg">{event.rsvp_summary.late}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="rsvp-count-no-response">
                      <span className="flex items-center text-gray-500">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        No Response
                      </span>
                      <span className="font-bold text-lg">{event.rsvp_summary.no_response ?? 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Parent RSVP Summary (for per-parent event types) */}
            {event.parent_rsvp_summary && (
              <Card data-testid="event-parent-rsvp-summary-card">
                <CardHeader>
                  <CardTitle className="text-navy text-lg">Parent RSVPs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium">Going</span>
                      <span className="font-bold text-lg" data-testid="parent-summary-attending">{event.parent_rsvp_summary.attending ?? 0}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-yellow-50 rounded-lg">
                      <span className="text-sm font-medium">Late</span>
                      <span className="font-bold text-lg" data-testid="parent-summary-late">{event.parent_rsvp_summary.late ?? 0}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-red/5 rounded-lg">
                      <span className="text-sm font-medium">Not Going</span>
                      <span className="font-bold text-lg" data-testid="parent-summary-absent">{event.parent_rsvp_summary.absent ?? 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attendance Summary — only when meaningful check-in data exists.
                Pre-event (no rows marked yet) the summary is all zeros except
                not_marked which duplicates the RSVP "Invited" count, so it's
                hidden until at least one player has been checked in. */}
            {event.attendance_enabled && event.attendance_summary
              && ((event.attendance_summary.present ?? 0)
                + (event.attendance_summary.absent ?? 0)
                + (event.attendance_summary.late ?? 0)
                + (event.attendance_summary.excused ?? 0) > 0) && (
              <Card data-testid="event-attendance-summary-card">
                <CardHeader>
                  <CardTitle className="text-navy text-lg flex items-center">
                    <UserCheck className="w-5 h-5 mr-2" />
                    Attendance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between" data-testid="att-count-invited">
                      <span className="text-navy">Invited</span>
                      <span className="font-bold">{event.attendance_summary.invited ?? event.invited_count ?? 0}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="att-count-present">
                      <span className="text-green-600">Present</span>
                      <span className="font-bold">{event.attendance_summary.present}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="att-count-absent">
                      <span className="text-red">Absent</span>
                      <span className="font-bold">{event.attendance_summary.absent}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="att-count-late">
                      <span className="text-yellow-600">Late</span>
                      <span className="font-bold">{event.attendance_summary.late}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="att-count-excused">
                      <span className="text-gray-600">Excused</span>
                      <span className="font-bold">{event.attendance_summary.excused}</span>
                    </div>
                    <div className="flex items-center justify-between" data-testid="att-count-not-marked">
                      <span className="text-gray-500">Not Marked</span>
                      <span className="font-bold">{event.attendance_summary.not_marked ?? 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Changes */}
            {event.change_logs?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-navy text-lg">Recent Changes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {event.change_logs.slice(0, 3).map(log => (
                      <div key={log.log_id} className="p-2 bg-gray-50 rounded">
                        <p className="font-medium text-navy capitalize">{log.change_type}</p>
                        <p className="text-gray-500 text-xs">
                          {format(parseISO(log.changed_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      {/* Phase J — Send Reminder Modal */}
      {isAdmin && (
        <Dialog open={showReminderModal} onOpenChange={(open) => { if (!sendingReminder) setShowReminderModal(open); }}>
          <DialogContent className="max-w-lg" data-testid="reminder-modal">
            <DialogHeader>
              <DialogTitle className="text-navy flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send Event Reminder
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <p className="font-semibold text-navy">{event?.title}</p>
                {event?.start_time && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {format(parseISO(event.start_time), 'EEE, MMM d, yyyy • h:mm a')}
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Audience</p>
                <div className="space-y-1.5">
                  {REMINDER_AUDIENCES.map((opt) => {
                    const count = reminderAudienceCount(opt.countKey);
                    const disabled = !event?.rsvp_required && opt.value !== 'all_invited';
                    const selected = reminderAudience === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => setReminderAudience(opt.value)}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${
                          selected
                            ? 'border-navy bg-navy/5'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={disabled ? 'RSVP-derived audiences require an RSVP-enabled event' : ''}
                        data-testid={`reminder-audience-${opt.value.replace('_', '-')}`}
                      >
                        <span className="flex items-center gap-2 text-sm text-navy">
                          <span aria-hidden>{opt.icon}</span>
                          <span className={selected ? 'font-bold' : 'font-medium'}>{opt.label}</span>
                        </span>
                        <span className="text-xs text-gray-500 font-mono">
                          {count} {count === 1 ? 'player' : 'players'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {!event?.rsvp_required && (
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    This event has no RSVP, so only &quot;All Invited&quot; is available.
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                  Title
                </p>
                <input
                  type="text"
                  value={reminderTitle}
                  onChange={(e) => setReminderTitle(e.target.value)}
                  placeholder="Reminder title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                  data-testid="reminder-title-input"
                />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                  Message
                </p>
                <textarea
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                  rows={4}
                  placeholder="Reminder message"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-sm"
                  data-testid="reminder-message-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Pre-filled from the event details. Edit freely before sending.
                </p>
              </div>

              {reminderToast && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    reminderToast.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red/5 border border-red/20 text-red'
                  }`}
                  data-testid="reminder-toast"
                >
                  {reminderToast.text}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowReminderModal(false)}
                disabled={sendingReminder}
                data-testid="reminder-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendReminder}
                disabled={
                  sendingReminder ||
                  !reminderTitle.trim() ||
                  !reminderMessage.trim()
                }
                className="bg-red hover:bg-red/90 text-white"
                data-testid="reminder-send-confirm"
              >
                {sendingReminder
                  ? 'Sending…'
                  : `Send to ~${reminderAudienceCount(REMINDER_AUDIENCES.find(a => a.value === reminderAudience)?.countKey)} parent${reminderAudienceCount(REMINDER_AUDIENCES.find(a => a.value === reminderAudience)?.countKey) === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </ParentLayout>
  );
};

export default EventDetailPage;
